import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { AlertTriangleIcon, CalculatorIcon, ClockIcon, FileTextIcon, PackageIcon, SparklesIcon, Trash2Icon } from 'lucide-react';
import { db } from '../firebase';
import { DictationPad } from '../components/mesterai/DictationPad';
import { MaterialPreset, QuoteLine, lineTotal, parseDictation, summarize } from '../utils/mesterAi';
const DEFAULT_HOURLY_RATE = 750;
function kr(value: number): string {
  return `${value.toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} kr`;
}
export function MesterAI() {
  const [transcript, setTranscript] = useState('');
  const [hourlyRate, setHourlyRate] = useState(String(DEFAULT_HOURLY_RATE));
  const [presets, setPresets] = useState<MaterialPreset[]>([]);
  const [lines, setLines] = useState<QuoteLine[] | null>(null);
  const [unparsed, setUnparsed] = useState<string[]>([]);

  // Standard materials are maintained by the admin under Information.
  useEffect(() => {
    const ref = doc(db, 'settings', 'standardMaterials');
    return onSnapshot(ref, snapshot => {
      const items = snapshot.exists() ? snapshot.data()?.items : [];
      setPresets(Array.isArray(items) ? items : []);
    });
  }, []);
  const breakdown = useMemo(() => lines ? summarize(lines, unparsed) : null, [lines, unparsed]);
  const handleCalculate = () => {
    const result = parseDictation({
      transcript,
      hourlyRate: parseFloat(hourlyRate) || 0,
      presets
    });
    setLines(result.lines);
    setUnparsed(result.unparsed);
  };
  const updateLine = (id: string, patch: Partial<QuoteLine>) => {
    setLines(current => current?.map(line => line.id === id ? {
      ...line,
      ...patch,
      needsPrice: patch.unitPrice !== undefined ? patch.unitPrice <= 0 : line.needsPrice
    } : line) ?? null);
  };
  const removeLine = (id: string) => {
    setLines(current => current?.filter(line => line.id !== id) ?? null);
  };
  const missingPrices = breakdown?.lines.filter(line => line.needsPrice).length ?? 0;
  return <div data-id="element-1651" data-mp="1d1zsp66qy6mn">
      <div className="mb-8" data-id="element-1652" data-mp="1d16tqre14lf2">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700" data-id="element-1653" data-mp="1d0x4x74q0kay">
          <SparklesIcon className="h-3.5 w-3.5" data-id="element-1654" data-mp="1ms1f7tjgbgzg" />
          Mester-AI
        </p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-stone-900" data-id="element-1655" data-mp="1mudxk7t4ftd1">
          Indtal en opgave, få tilbuddet
        </h1>
        <p className="mt-2 max-w-2xl text-stone-500" data-id="element-1656" data-mp="1vs7thhmz4kzo">
          Beskriv opgaven med din stemme. Mester-AI genkender timer og
          materialer, henter priserne fra dine standard materialer og lægger
          tilbuddet sammen.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_400px]" data-id="element-1657" data-mp="1gjc78t9s6c4">
        {/* Input */}
        <div className="space-y-5" data-id="element-1658" data-mp="2d44w5vn945ip">
          <DictationPad transcript={transcript} onTranscriptChange={setTranscript} data-id="element-1659" data-mp="2i1oaswnv2kb6" />

          <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-1660" data-mp="248oxn1bjx26s">
            <label htmlFor="mester-rate" className="block text-sm font-medium text-stone-700" data-id="element-1661" data-mp="28atm7ln578mk">
              Din timepris
            </label>
            <div className="mt-1.5 flex flex-wrap items-center gap-3" data-id="element-1662" data-mp="1zane6fa7r0us">
              <input id="mester-rate" type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} className="w-40 rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-1663" data-mp="1z8kdn1bfppbt" />
              <p className="text-sm text-stone-500" data-id="element-1664" data-mp="3jpyur69yg1e3">
                {presets.length > 0 ? `${presets.length} standard materialer klar til prissætning` : 'Ingen standard materialer endnu — tilføj dem under Information'}
              </p>
            </div>

            <button onClick={handleCalculate} disabled={transcript.trim().length === 0} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-stone-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400" data-id="element-1665" data-mp="2l03l2v6jv7yu">
              <CalculatorIcon className="h-4 w-4" data-id="element-1666" data-mp="22byp49w4wral" />
              Beregn tilbud
            </button>
          </div>

          {/* Parsed lines */}
          {breakdown && <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white" data-id="element-1667" data-mp="1sd93q7514opl">
              <header className="flex flex-wrap items-center gap-3 border-b border-stone-100 px-5 py-4" data-id="element-1668" data-mp="1oavzxo9uf0vg">
                <h2 className="min-w-0 flex-1 text-base font-semibold text-stone-900" data-id="element-1669" data-mp="3u4wnl63djv0">
                  Tilbudslinjer
                </h2>
                {missingPrices > 0 && <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700" data-id="element-1670" data-mp="cub871i8c9rj">
                    <AlertTriangleIcon className="h-3.5 w-3.5" data-id="element-1671" data-mp="hrtkn1vgh74a" />
                    {missingPrices} mangler pris
                  </span>}
              </header>

              {breakdown.lines.length === 0 ? <p className="px-5 py-10 text-center text-sm text-stone-500" data-id="element-1672" data-mp="3l7s9hay6toz8">
                  Mester-AI fandt ingen linjer. Prøv at nævne timer og
                  materialer med antal.
                </p> : <ul className="divide-y divide-stone-100" data-id="element-1673" data-mp="2xkk1gl9dk1ti">
                  {breakdown.lines.map(line => <li key={line.id} className="px-5 py-4" data-id="element-1674" data-mp="32iu23bdvbumi">
                      <div className="flex items-start gap-3" data-id="element-1675" data-mp="32h0pzgkhki74">
                        <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${line.kind === 'labour' ? 'bg-brand-50 text-brand-600' : 'bg-stone-100 text-stone-500'}`} data-id="element-1676" data-mp="2uhcuiea3wbbf">
                          {line.kind === 'labour' ? <ClockIcon className="h-[18px] w-[18px]" data-id="element-1677" data-mp="2blw8rmxf27q1" /> : <PackageIcon className="h-[18px] w-[18px]" data-id="element-1678" data-mp="27kb24jbh6poa" />}
                        </span>

                        <div className="min-w-0 flex-1" data-id="element-1679" data-mp="2gj693xmcg3sn">
                          <input value={line.description} onChange={e => updateLine(line.id, {
                    description: e.target.value
                  })} aria-label="Beskrivelse" className="w-full border-none bg-transparent p-0 text-sm font-medium text-stone-900 outline-none focus:ring-0" data-id="element-1680" data-mp="2ghlbfz4qg4qk" />
                          <p className="mt-0.5 truncate text-xs italic text-stone-400" data-id="element-1681" data-mp="19op77czvqkb8">
                            “{line.source}”
                          </p>
                        </div>

                        <button onClick={() => removeLine(line.id)} aria-label={`Fjern ${line.description}`} className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-1682" data-mp="1xgnme82g8ztn">
                          <Trash2Icon className="h-4 w-4" data-id="element-1683" data-mp="2gdfb4x0rg753" />
                        </button>
                      </div>

                      <div className="mt-3 flex flex-wrap items-end gap-3 pl-12" data-id="element-1684" data-mp="2l4428b8j323p">
                        <label className="w-24" data-id="element-1685" data-mp="l1cnt3os1f1r">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-stone-400" data-id="element-1686" data-mp="l377keobb149">
                            Antal
                          </span>
                          <input type="number" step="0.5" value={line.quantity} onChange={e => updateLine(line.id, {
                    quantity: parseFloat(e.target.value) || 0
                  })} className="w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-1687" data-mp="bckyl3b3p8uj" />
                        </label>
                        <span className="pb-2 text-sm text-stone-500" data-id="element-1688" data-mp="3eqfe2ex5zzht">
                          {line.unit}
                        </span>
                        <label className="w-32" data-id="element-1689" data-mp="1q9ffypazgjud">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-stone-400" data-id="element-1690" data-mp="1q6szg0646u00">
                            Pris pr. enhed
                          </span>
                          <input type="number" step="0.01" value={line.unitPrice} onChange={e => updateLine(line.id, {
                    unitPrice: parseFloat(e.target.value) || 0
                  })} className={`w-full rounded-lg border px-2.5 py-1.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500 ${line.needsPrice ? 'border-amber-300 bg-amber-50' : 'border-stone-200'}`} data-id="element-1691" data-mp="1z89bsdghp66v" />
                        </label>
                        <p className="ml-auto pb-1.5 text-sm font-semibold text-stone-900" data-id="element-1692" data-mp="1eknz16r9xofb">
                          {kr(lineTotal(line))}
                        </p>
                      </div>
                    </li>)}
                </ul>}

              {breakdown.unparsed.length > 0 && <div className="border-t border-stone-100 bg-stone-50 px-5 py-4" data-id="element-1693" data-mp="1xhp6qzc7q2my">
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1694" data-mp="22g8pkqvieyb3">
                    Ikke forstået
                  </p>
                  <ul className="mt-1.5 space-y-1" data-id="element-1695" data-mp="277h2hww1t1t0">
                    {breakdown.unparsed.map((clause, index) => <li key={index} className="text-sm italic text-stone-500" data-id="element-1696" data-mp="2c84r6bwt16k2" data-mpi={index}>
                        “{clause}”
                      </li>)}
                  </ul>
                </div>}
            </div>}
        </div>

        {/* Quote summary */}
        <div className="xl:sticky xl:top-24 xl:self-start" data-id="element-1697" data-mp="9kpqxsp473l3">
          <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white" data-id="element-1698" data-mp="5hjj4gmct4ci">
            <header className="bg-stone-900 px-5 py-4" data-id="element-1699" data-mp="hwwxhojsdrk">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50" data-id="element-1700" data-mp="ksydfzltkkr">
                Tilbud
              </p>
              <p className="mt-1 text-2xl font-bold text-white" data-id="element-1701" data-mp="t7vu60cwbh7b">
                {breakdown ? kr(breakdown.total) : '—'}
              </p>
              <p className="mt-0.5 text-xs text-white/60" data-id="element-1702" data-mp="o8stst7v1z5o">inkl. moms</p>
            </header>

            {breakdown ? <>
                <dl className="divide-y divide-stone-100" data-id="element-1703" data-mp="eir8282zcstx">
                  {[{
                label: `Arbejdstid (${breakdown.labourHours.toLocaleString('da-DK')} t)`,
                value: kr(breakdown.labourTotal)
              }, {
                label: 'Materialer',
                value: kr(breakdown.materialTotal)
              }, {
                label: 'Subtotal',
                value: kr(breakdown.subtotal),
                strong: true
              }, {
                label: 'Moms 25 %',
                value: kr(breakdown.vat)
              }].map(row => <div key={row.label} className="flex items-center justify-between gap-3 px-5 py-3" data-id="element-1704" data-mp="2hr9npzxo6b2u">
                      <dt className={`text-sm ${row.strong ? 'font-semibold text-stone-900' : 'text-stone-500'}`} data-id="element-1705" data-mp="2mo06ww4rtmsh">
                        {row.label}
                      </dt>
                      <dd className={`text-sm ${row.strong ? 'font-semibold text-stone-900' : 'text-stone-700'}`} data-id="element-1706" data-mp="byl5o4u0lafh">
                        {row.value}
                      </dd>
                    </div>)}
                </dl>

                <div className="border-t border-stone-100 p-5" data-id="element-1707" data-mp="q31xmc74eqn6">
                  <button className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1708" data-mp="q1h50s4d9hry">
                    <FileTextIcon className="h-4 w-4" data-id="element-1709" data-mp="zvhe152rr75y" />
                    Opret tilbud
                  </button>
                  {missingPrices > 0 && <p className="mt-2 text-center text-xs text-amber-600" data-id="element-1710" data-mp="19sm4hh3ndosk">
                      Udfyld de manglende priser først
                    </p>}
                </div>
              </> : <p className="px-5 py-10 text-center text-sm text-stone-500" data-id="element-1711" data-mp="3bkqqkbygtrvd">
                Indtal opgaven og tryk “Beregn tilbud” for at se summen.
              </p>}
          </div>
        </div>
      </div>
    </div>;
}